import React, { PropsWithChildren, useEffect } from 'react';

import { Button, Flex, Text } from '@opendoor/bricks/core';
import { getRailsEnv } from '@opendoor/shared-fe/build/railsEnv';

import { CheckIcon } from '../../components/icons/CheckIcon';
import { useVirtualAssessmentData } from '../../graphql/athena/queries/useVirtualAssessmentData';
import { customerDataProps } from '../../helpers/getAllLeadData';

interface QuestionnaireTaskProps {
  sellerInputUUID: string;
  sellerLeadToken: string;
  customerData: customerDataProps;
}
export const PRODUCTION_ALA_GOOGLE_ASSESSMENT_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLSdebDQvphKnOePx1SWIhEEEVXXFN2BJuzzdeGRksEIflHHzpQ/viewform?usp=pp_url';
export const STAGING_ALA_GOOGLE_ASSESSMENT_FORM_URL_PREFIX =
  'https://docs.google.com/forms/d/e/1FAIpQLScCnTOuddATXGYq_g64AWcc6Zb5rvh7AedQ9S9B2r-WjP7D7A/viewform?usp=pp_url';
export const FLIP_TOKEN_FORM_ENTRY = 'entry.1387189579';
export const EMAIL_FORM_ENTRY = 'entry.1100801854';
export const NAME_FORM_ENTRY = 'entry.678167604';

const getFormUrlPrefix = (env: string) => {
  return env !== 'production'
    ? STAGING_ALA_GOOGLE_ASSESSMENT_FORM_URL_PREFIX
    : PRODUCTION_ALA_GOOGLE_ASSESSMENT_FORM_URL_PREFIX;
};

export const QuestionnaireTask: React.FC<QuestionnaireTaskProps & PropsWithChildren> = ({
  sellerInputUUID,
  sellerLeadToken,
  customerData,
}) => {
  const {
    details,
    refetch: refetchVirtualAssessmentData,
    loading: vaLoading,
  } = useVirtualAssessmentData(sellerInputUUID);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const ssvaQuestionnaireSubmitted =
    details.latestSelfServeVirtualAssessment?.ssvaQuestionnaireSubmitted;
  const ssvaQuestionnaireDisabled =
    ssvaQuestionnaireSubmitted ||
    !(
      details?.latestSelfServeVirtualAssessment?.ssvaInteriorRecordingsSubmitted &&
      details?.latestSelfServeVirtualAssessment?.ssvaFrontyardRecordingsSubmitted &&
      details?.latestSelfServeVirtualAssessment?.ssvaBackyardRecordingsSubmitted
    );

  // Compute SSVA questionnaire form link
  const formUrlPrefix = getFormUrlPrefix(getRailsEnv());
  const formPrefill = encodeURI(
    `&${FLIP_TOKEN_FORM_ENTRY}=${sellerLeadToken ?? ''}&${EMAIL_FORM_ENTRY}=${
      customerData?.email ?? ''
    }&${NAME_FORM_ENTRY}=${customerData?.name ?? ''}`,
  );
  const ssvaFormLink = `${formUrlPrefix}${formPrefill}`;

  useEffect(() => {
    /**
     * Refetch VA data if user switched tabs then switched back.
     * This is for making sure the page has the up-to-date SSVA status.
     */
    const visibilityListener = async () => {
      if (document.visibilityState === 'visible') {
        setIsRefetching(true);
        await refetchVirtualAssessmentData();
        setIsRefetching(false);
      }
    };

    document.addEventListener('visibilitychange', visibilityListener);
    return () => {
      document.removeEventListener('visibilitychange', visibilityListener);
    };
  }, [refetchVirtualAssessmentData]);

  if (vaLoading) return null;

  return (
    <Flex alignItems={'center'} marginBottom={5} gap={2}>
      <div style={{ width: '22px' }}>{ssvaQuestionnaireSubmitted && <CheckIcon />}</div>
      <Text flex={1} fontWeight={'semibold'}>
        Answer a few questions:
        <Text flex={1} fontWeight={'normal'}>
          (1 min)
        </Text>
      </Text>
      {!ssvaQuestionnaireSubmitted && (
        <Button
          analyticsName="ssva-questionnaire-button"
          aria-label="questionnaire button"
          onClick={ssvaFormLink ? () => window.open(ssvaFormLink, '_blank') : () => {}}
          size="sm"
          variant="secondary"
          width={'unset'}
          disabled={ssvaQuestionnaireDisabled || isRefetching}
        >
          Click Here
        </Button>
      )}
    </Flex>
  );
};
